import { type ComputedRef, computed } from 'vue';
import { type RouteRecordRaw, useRouter } from 'vue-router';

/**
 * Composable for detecting and managing admin zone context.
 *
 * This composable provides reactive state and utilities for determining
 * whether the user is currently in the admin zone (routes starting with /admin)
 * and filtering routes accordingly.
 *
 * @returns Object containing admin zone detection and route filtering utilities
 */
export function useAdminZone() {
  const router = useRouter();

  /**
   * Computed property that determines if the current route is in the admin zone.
   * Admin zone is defined as any route whose path starts with '/admin'.
   */
  const isInAdminZone: ComputedRef<boolean> = computed(() => {
    return router.currentRoute.value.path.startsWith('/admin');
  });

  /**
   * Filters routes to return only admin routes.
   * Admin routes are identified by having `requiresAdmin: true` in their meta.
   */
  const adminRoutes: ComputedRef<RouteRecordRaw[]> = computed(() => {
    const definedRoutes = router.options.routes;
    const mainAppRoutes: RouteRecordRaw[] =
      (definedRoutes?.[0]?.children as RouteRecordRaw[] | undefined) ?? [];

    return mainAppRoutes.filter((route) => {
      return route.meta?.requiresAdmin === true;
    });
  });

  /**
   * Filters routes to return only main app routes (non-admin routes).
   * Main app routes are identified by NOT having `requiresAdmin: true` in their meta.
   */
  const mainAppRoutes: ComputedRef<RouteRecordRaw[]> = computed(() => {
    const definedRoutes = router.options.routes;
    const allRoutes: RouteRecordRaw[] =
      (definedRoutes?.[0]?.children as RouteRecordRaw[] | undefined) ?? [];

    return allRoutes.filter((route) => {
      return route.meta?.requiresAdmin !== true;
    });
  });

  /**
   * Returns the appropriate routes based on the current context (admin zone or main app).
   * Automatically switches between admin routes and main app routes.
   */
  const currentContextRoutes: ComputedRef<RouteRecordRaw[]> = computed(() => {
    return isInAdminZone.value ? adminRoutes.value : mainAppRoutes.value;
  });

  return {
    isInAdminZone,
    adminRoutes,
    mainAppRoutes,
    currentContextRoutes,
  };
}
